import {flatten, partition} from 'lodash';

import {Dictionary, isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {isTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {AnswerModel, ApiAnswers} from '../../../../models/api/answerModel';
import {fetchApiAsync} from '../../../../services/http/http';

/*
 * Types.
 */

interface FetchProps {
  companyUrl: string;
  namespace: string;
  cancelToken: CancelToken;
}

interface FetchByNamespacesProps {
  companyUrl: string;
  namespaces: ReadonlyArray<string>;
  cancelToken: CancelToken;
}

interface FetchByNamespacesWithCacheProps {
  companyUrl: string;
  namespaces: ReadonlyArray<string>;
  cancelToken: CancelToken;
  answerMapCache: Dictionary<ReadonlyArray<AnswerModel>>; // namespace, answers.
}

/*
 * Service.
 */

export class AnswerService {
  private fetchCache = new Map<string, Promise<ReadonlyArray<AnswerModel>>>();

  private async fetch({cancelToken, companyUrl, namespace}: FetchProps) {
    const url = `${companyUrl}/namespaces/${namespace}/canned_answers`;

    const cachedItem = this.fetchCache.get(url);

    if (cachedItem) {
      return cachedItem;
    }

    const answersPromise = fetchApiAsync(ApiAnswers, url, {cancelToken});
    answersPromise.catch(() => {
      this.fetchCache.delete(url);
      return [];
    });

    this.fetchCache.set(url, answersPromise);
    return answersPromise;
  }

  private async fetchByNamespaces({
    companyUrl,
    namespaces,
    cancelToken,
  }: FetchByNamespacesProps): Promise<ReadonlyArray<AnswerModel>> {
    return Promise.all(
      namespaces.map(async (namespace) => this.fetch({cancelToken, companyUrl, namespace})),
    ).then((answers) => flatten(answers));
  }

  async fetchByNamespacesWithCache({
    cancelToken,
    companyUrl,
    namespaces,
    answerMapCache,
  }: FetchByNamespacesWithCacheProps) {
    const [loadFromCache, maybeLoadFromApi] = partition(namespaces, (namespace) =>
      Boolean(answerMapCache[namespace]),
    );

    // Team answers are already loaded in Redux.
    // This avoid to fetch teams with no answers.
    const loadFromApi = maybeLoadFromApi.filter(isTeammateNamespace);

    const apiItems = await this.fetchByNamespaces({companyUrl, cancelToken, namespaces: loadFromApi});
    const cacheItems = flatten(loadFromCache.map((namespace) => answerMapCache[namespace]).filter(isDefined));

    return [...apiItems, ...cacheItems];
  }
}
